import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { createKpiDetailsDialogConfig } from 'src/app/components/main/employees/performance/my-metrics/kpi-details-dialog/kpi-details-dialog.config';
import { DayOffTypeEnum, getDayOffTypeEmoji, getDayOffTypeLabel } from 'src/app/enums/day-off-type.enum';
import { ProductionKeyPerformanceIndicatorEvaluation } from 'src/app/models/production-key-performance-indicator-evaluation.model';
import {
  ScheduleAndRemoteWorkComplianceCalendarDialogData,
  ScheduleAndRemoteWorkComplianceDay,
  ScheduleAndRemoteWorkComplianceDayCustom,
  ScheduleAndRemoteWorkComplianceEvaluatorData,
} from 'src/app/models/schedule-and-remote-work-compliance-kpi-data.model';
import { ScheduleAndRemoteWorkComplianceCalendarDialogComponent } from './schedule-and-remote-work-compliance-calendar-dialog/schedule-and-remote-work-compliance-calendar-dialog.component';

@Component({
  selector: 'app-schedule-and-remote-work-compliance-details',
  imports: [CommonModule, DecimalPipe, MatButtonModule, MatIconModule],
  templateUrl: './schedule-and-remote-work-compliance-details.component.html',
  styleUrl: './schedule-and-remote-work-compliance-details.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleAndRemoteWorkComplianceDetailsComponent {
  private readonly dialog = inject(MatDialog);

  current_month_evaluation = input<ProductionKeyPerformanceIndicatorEvaluation | null>(null);

  compliance_data = computed((): ScheduleAndRemoteWorkComplianceEvaluatorData | null => {
    return resolve_schedule_compliance_evaluator_data(this.current_month_evaluation());
  });

  readonly days = computed(() => toScheduleAndRemoteWorkComplianceDayCustoms(this.compliance_data()?.days));

  readonly streak_emoji = computed((): string | null => {
    const data = this.compliance_data();

    return data ? get_streak_emoji(data.current_streak) : null;
  });

  readonly streak_unit = computed((): string => {
    return (this.compliance_data()?.current_streak ?? 0) === 1 ? 'día' : 'días';
  });

  readonly has_calendar_data = computed((): boolean => this.days().length > 0);

  openCalendarDialog(): void {
    const evaluation = this.current_month_evaluation();
    const compliance_data = this.compliance_data();
    const days = this.days();

    if (!evaluation || !compliance_data || days.length === 0) {
      return;
    }

    this.dialog.open(
      ScheduleAndRemoteWorkComplianceCalendarDialogComponent,
      createKpiDetailsDialogConfig<ScheduleAndRemoteWorkComplianceCalendarDialogData>({
        days,
        average_offset_minutes: compliance_data.average_offset_minutes,
        current_streak: compliance_data.current_streak,
        streak_emoji: this.streak_emoji(),
        streak_unit: this.streak_unit(),
        tooltip_summary: compliance_data.tooltip_summary,
        month_start_date: evaluation.month_start_date,
        month_end_date: evaluation.month_end_date,
      })
    );
  }
}

function resolve_schedule_compliance_evaluator_data(
  evaluation: { data?: unknown } | null | undefined
): ScheduleAndRemoteWorkComplianceEvaluatorData | null {
  const raw_data = evaluation?.data;

  if (raw_data == null || typeof raw_data !== 'object') {
    return null;
  }

  const candidate = raw_data as Record<string, unknown>;
  const nested_data = candidate['data'];

  if (Array.isArray(candidate['days']) || candidate['days'] != null) {
    return candidate as unknown as ScheduleAndRemoteWorkComplianceEvaluatorData;
  }

  if (nested_data != null && typeof nested_data === 'object') {
    return nested_data as ScheduleAndRemoteWorkComplianceEvaluatorData;
  }

  return null;
}

function get_streak_emoji(streak: number): string | null {
  const conditions: Array<{ predicate: (value: number) => boolean; emoji: string }> = [
    { predicate: value => value >= 60, emoji: '🐐' },
    { predicate: value => value >= 40, emoji: '🤑' },
    { predicate: value => value >= 25, emoji: '👑' },
    { predicate: value => value >= 15, emoji: '🚀' },
    { predicate: value => value >= 10, emoji: '🔥' },
    { predicate: value => value >= 5, emoji: '🎯' },
    { predicate: value => value >= 2, emoji: '🤨' },
  ];

  return conditions.find(condition => condition.predicate(streak))?.emoji ?? null;
}

function toScheduleAndRemoteWorkComplianceDayCustoms(days: unknown): ScheduleAndRemoteWorkComplianceDayCustom[] {
  if (days == null) {
    return [];
  }

  const list = Array.isArray(days) ? days : Object.values(days);

  return list.flatMap((item): ScheduleAndRemoteWorkComplianceDayCustom[] => {
    if (!is_compliance_day(item)) {
      return [];
    }

    const day_off_type = is_schedule_compliance_day_off_type(item.day_off_type) ? item.day_off_type : null;
    const day_is_off = day_off_type !== null;

    return [
      {
        ...item,
        day_off_type,
        is_day_off: day_is_off,
        is_vacation: day_off_type === DayOffTypeEnum.Vacation,
        is_holiday: day_off_type === DayOffTypeEnum.Holiday,
        is_absence: day_off_type === DayOffTypeEnum.Absence,
        schedule_mark: day_is_off ? '' : status_mark(item.is_schedule_ok),
        remote_mark: day_is_off ? '' : status_mark(item.is_remote_ok),
        day_off_emoji: day_is_off ? getDayOffTypeEmoji(day_off_type) : null,
        day_off_label: day_is_off ? getDayOffTypeLabel(day_off_type) : null,
      },
    ];
  });
}

function status_mark(is_ok: boolean): string {
  return is_ok ? '✅' : '❌';
}

function is_schedule_compliance_day_off_type(value: unknown): value is DayOffTypeEnum {
  return typeof value === 'string' && new Set<string>(Object.values(DayOffTypeEnum)).has(value);
}

function is_compliance_day(value: unknown): value is ScheduleAndRemoteWorkComplianceDay {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return typeof (value as { date?: unknown }).date === 'string';
}
